import React from "react";
import OptimizedComponent from "../../../components/optimizedComponent";
import { LoadingButton } from "../../../components/formcontrols/imports";
import { routingService, translateService, authService, sessionService, signService } from "../../../services/imports";
import linkSession, { LINK_KIND } from "../../../services/linkSessionService";
import oidcService from "../../../services/oidcService";
import shareService from "../../../services/shareService";
import startDocumentSession from "./startDocumentSession";

// An organization share's link (share-prompt task 17): the exchange answers
// CORPORATE_LOGIN_REQUIRED, the secret and the exchange's extras wait here
// (this tab's sessionStorage, never the address or localStorage) through the
// corporate login, then the redeem opens the document signed in.
const ORG_SHARE_KEY = "validatege.organization-share";

const readOrganizationShare = () => {
  try {
    const raw = sessionStorage.getItem(ORG_SHARE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const keepOrganizationShare = (share) => {
  try {
    sessionStorage.setItem(ORG_SHARE_KEY, JSON.stringify(share));
  } catch (e) {
    /* storage unavailable: the login comes back to the unknown-link page */
  }
};

const dropOrganizationShare = () => {
  try {
    sessionStorage.removeItem(ORG_SHARE_KEY);
  } catch (e) {
    /* nothing kept */
  }
};

// the corporate login in this tab only: a validatege session elsewhere stays
const corporateLogin = (share) =>
  authService.login({
    realm: "corp",
    loginHint: share.login_hint,
    tabOnly: true,
    returnTo: `${globalConfig.base_URL}l`,
  });

// another corporate account: Keycloak refuses a second user while its
// corporate SSO session holds one, so that session ends first (in every tab
// that uses it) and the page comes back here to sign in again
const otherCorporateAccount = (share) =>
  oidcService.activeRealm() === "corp" && oidcService.currentUser()
    ? oidcService.logout({ returnTo: "l" })
    : corporateLogin(share);

// Why a document link gives no document (backend sharing-runbook §5; the
// exchange's `reason`, plus SESSION_ENDED for a second 401 with a fresh token
// and NETWORK for an exchange that got no answer). `kind` tells an e-mailed
// link from a printed public view, whose expiry has nobody to ask.
export function LinkRefusal({ refusal, onRetry, retrying }) {
  const t = (k, ...a) => translateService.t(`link.${k}`, ...a);
  const extras = refusal.extras || {};
  let title;
  let text;
  // a free validation's expired result link: its document is kept, pending,
  // for the address it was sent to - signed in, it waits in the dashboard's
  // "waiting to be opened" (task 22). A share's link names its sharer
  // instead; an organization share's comes from the redeem.
  let signInAs = null;

  switch (refusal.reason) {
    case "LINK_EXPIRED":
      if (refusal.kind === LINK_KIND.publicView) {
        title = t("publicExpiredTitle");
        text = t("publicExpiredText");
      } else {
        title = t("expiredTitle");
        if (extras.sharer_name) text = t("expiredAskSharer", extras.sharer_name, extras.recipient_email || "");
        else if (extras.recipient_email && !refusal.organizationShare) {
          text = t("expiredSignIn", extras.recipient_email);
          signInAs = extras.recipient_email;
        } else if (extras.recipient_email) text = t("expiredSentTo", extras.recipient_email);
        else text = t("expiredAskSender");
      }
      break;
    case "DOCUMENT_DELETED":
      title = t("deletedTitle");
      text = t("deletedText");
      break;
    case "SHARE_WITHDRAWN":
      title = t("withdrawnTitle");
      text = extras.sharer_name ? t("withdrawnBy", extras.sharer_name) : t("withdrawnText");
      break;
    case "CORPORATE_LOGIN_REQUIRED":
      title = t("corporateTitle");
      text = extras.sharer_name
        ? t("corporateShared", extras.sharer_name, extras.login_hint || "", extras.organization_name || "")
        : t("corporateSharedNoSharer", extras.login_hint || "", extras.organization_name || "");
      break;
    case "SESSION_ENDED":
      title = t("endedTitle");
      text = t("endedText");
      break;
    case "NETWORK":
      title = t("networkTitle");
      text = t("networkText");
      break;
    case "LINK_UNKNOWN":
      title = t("unknownTitle");
      text = t("unknownText");
      break;
    case "DOCUMENT_UNKNOWN":
      // a signed-in read answered 400: no such document, or an old numeric
      // id from a bookmark - the backend's words are never shown here
      title = t("documentUnknownTitle");
      text = t("documentUnknownText");
      break;
    case "READ_FAILED":
      // a signed-in read refused for another reason: the backend's title
      title = t("readFailedTitle");
      text = refusal.description || t("readFailedText");
      break;
    default:
      // a reason this page does not know yet: the backend's own words
      title = t("unknownTitle");
      text = refusal.description || t("unknownText");
  }

  return (
    <div className={`link-page container ${translateService.key}`}>
      <div className="link-page__box" role="alert">
        <span className="title">{title}</span>
        <p>{text}</p>
        <div className="buttons">
          {onRetry && (
            <LoadingButton
              type="button"
              className="button background-color-two color-white"
              loading={retrying}
              onClick={onRetry}
            >
              {t("retry")}
            </LoadingButton>
          )}
          {signInAs && (
            <LoadingButton
              type="button"
              className="button background-color-two color-white"
              onClick={() =>
                authService.login({ realm: "b2c", loginHint: signInAs, returnTo: `${globalConfig.base_URL}dashboard` })
              }
            >
              {t("signIn")}
            </LoadingButton>
          )}
          <LoadingButton
            type="button"
            className="button background-color-one color-white"
            onClick={() => routingService.push(routingService.routeNames.default)}
          >
            {t("home")}
          </LoadingButton>
        </div>
      </div>
    </div>
  );
}

// An organization share between the exchange and the document: whom to sign
// in as, and each refusal of the redeem (problem+json without extras: the
// exchange's are used). step: "login" | "notForAccount" | "notMember" |
// "corporateAgain" | "unavailable" | "failed".
function OrganizationShareStep({ share, step, message, busy, onLogin, onOtherAccount, onRetry }) {
  const t = (k, ...a) => translateService.t(`link.${k}`, ...a);
  const organization = share.organization_name || "";
  const shared = share.sharer_name
    ? t("corporateShared", share.sharer_name, share.login_hint || "", organization)
    : t("corporateSharedNoSharer", share.login_hint || "", organization);
  let title = t("corporateTitle");
  let text = shared;
  let primary = { label: t("corporateSignIn", organization), onClick: onLogin };

  switch (step) {
    case "notForAccount":
      title = t("notForAccountTitle");
      text = t("notForAccountText", share.login_hint || "", oidcService.email() || "");
      primary = { label: t("otherAccount"), onClick: onOtherAccount };
      break;
    case "notMember":
      title = t("notMemberTitle");
      text = t("notMemberText", organization);
      primary = { label: t("otherAccount"), onClick: onOtherAccount };
      break;
    case "corporateAgain":
      text = `${t("corporateAgainText", organization)} ${shared}`;
      break;
    case "unavailable":
      title = t("unavailableTitle");
      text = message || t("unavailableText");
      primary = { label: t("retry"), onClick: onRetry };
      break;
    case "failed":
      title = t("redeemFailedTitle");
      text = message || t("networkText");
      primary = { label: t("retry"), onClick: onRetry };
      break;
    default:
      break;
  }

  return (
    <div className={`link-page container ${translateService.key}`}>
      <div className="link-page__box" role="alert">
        <span className="title">{title}</span>
        <p>{text}</p>
        <div className="buttons">
          <LoadingButton
            type="button"
            className="button background-color-two color-white"
            loading={busy}
            onClick={primary.onClick}
          >
            {primary.label}
          </LoadingButton>
          <LoadingButton
            type="button"
            className="button background-color-one color-white"
            onClick={() => routingService.push(routingService.routeNames.default)}
          >
            {t("home")}
          </LoadingButton>
        </div>
      </div>
    </div>
  );
}

// {web}/l#vs-<secret>: share invitations and free-validation result e-mails.
// The secret is taken from the fragment and the fragment leaves the address at
// once; the exchange fills this tab's slot and the document opens. Also the
// page every ended link session lands on (linkSessionService.end), and where
// an organization share's corporate login comes back to for the redeem.
export default class DocumentLinkPage extends OptimizedComponent {
  constructor(props) {
    super(props);
    this.state = { refusal: null, retrying: false, organizationShare: null, step: null, message: null, busy: false };
    this.value = null;
  }

  componentDidMount() {
    const raw = window.location.hash ? window.location.hash.slice(1) : "";
    if (raw) {
      this.value = decodeURIComponent(raw);
      window.history.replaceState(window.history.state, "", window.location.pathname + window.location.search);
      this.open();
      return;
    }
    const pending = readOrganizationShare();
    if (pending) {
      this.continueOrganizationShare(pending);
      return;
    }
    const refusal = linkSession.takeRefusal();
    this.setState({ refusal: refusal || { reason: "LINK_UNKNOWN", kind: LINK_KIND.share } });
  }

  // ---- the organization share ------------------------------------------------

  // signed in with a corporate login (this tab's or the shared one): the
  // redeem; else whom to sign in as
  continueOrganizationShare = (share) => {
    this.setState({ organizationShare: share, refusal: null });
    const corporate = oidcService.activeRealm() === "corp" && !!oidcService.currentUser();
    if (corporate) this.redeem(share);
    else this.setState({ step: "login" });
  };

  // POST /shares/redeem {secret} with the corporate token (never a slot's:
  // an organization share fills none); 200 the same on a repeat
  redeem = (share) => {
    this.setState({ busy: true, step: this.state.step || "redeeming", message: null });
    shareService.redeem(share.secret).subscribe(
      (res) => {
        dropOrganizationShare();
        // switch into and open by the exposed ids
        const { docExposedId: docId, workspaceExposedId: workspaceId } = res.data || {};
        // the primary workspace may be new: read the lists, no "first time"
        // question - the redeem was the explicit act
        sessionService
          .load()
          .catch(() => {})
          .then(() => {
            sessionService.switchTo(workspaceId);
            routingService.replace(routingService.routeNames.dashboardView, { params: { docId } });
          });
      },
      (e) => this.redeemRefused(share, e)
    );
  };

  redeemRefused = (share, e) => {
    const status = e && e.response && e.response.status;
    const type = signService.problemCode(e);
    const message = signService.problemMessage(e, null);
    const extras = {
      sharer_name: share.sharer_name,
      recipient_email: share.login_hint,
    };
    const page = (reason) => {
      dropOrganizationShare();
      this.setState({
        busy: false,
        organizationShare: null,
        refusal: { reason, extras, description: message, kind: LINK_KIND.share, organizationShare: true },
      });
    };
    if (status === 403 && type === "SHARE_NOT_FOR_ACCOUNT") return this.setState({ busy: false, step: "notForAccount" });
    if (status === 403 && type === "ORGANIZATION_MEMBERSHIP_REQUIRED") return this.setState({ busy: false, step: "notMember" });
    if (status === 403 && type === "CORPORATE_LOGIN_REQUIRED") return this.setState({ busy: false, step: "corporateAgain" });
    if (status === 410 && type === "LINK_EXPIRED") return page("LINK_EXPIRED");
    if (status === 403 && type === "SHARE_WITHDRAWN") return page("SHARE_WITHDRAWN");
    if (status === 410 && type === "DOCUMENT_DELETED") return page("DOCUMENT_DELETED");
    if (status === 404) return page("LINK_UNKNOWN");
    // 503 ORGANIZATIONS_UNAVAILABLE: try again after a renewal; the secret stays
    if (status === 503 && type === "ORGANIZATIONS_UNAVAILABLE")
      return this.setState({ busy: false, step: "unavailable", message });
    this.setState({ busy: false, step: "failed", message });
  };

  retryRedeem = () => {
    const share = this.state.organizationShare;
    this.setState({ busy: true });
    sessionService.renew().then(() => this.redeem(share), () => this.redeem(share));
  };

  kind = () => (this.value && this.value.startsWith("vw-") ? LINK_KIND.publicView : LINK_KIND.share);

  open = () => {
    const kind = this.kind();
    this.setState({ retrying: true });
    linkSession.open(this.value, kind).then(
      (slot) => startDocumentSession(slot),
      (refusal) => {
        // an organization share: no token, no slot - the corporate login
        if (refusal.reason === "CORPORATE_LOGIN_REQUIRED" && kind === LINK_KIND.share) {
          const share = { secret: this.value, ...(refusal.extras || {}) };
          keepOrganizationShare(share);
          // an earlier document link in this tab would send its token to the
          // redeem: drop it and start the page afresh on the tab's own session
          if (linkSession.has()) {
            linkSession.clear();
            window.location.assign(`${globalConfig.base_URL}l`);
            return;
          }
          this.setState({ retrying: false });
          this.continueOrganizationShare(share);
          return;
        }
        this.setState({
          retrying: false,
          refusal: {
            reason: refusal.status === 0 ? "NETWORK" : refusal.reason || "LINK_UNKNOWN",
            extras: refusal.extras,
            description: refusal.description,
            kind,
          },
        });
      }
    );
  };

  render() {
    const { refusal, organizationShare, step } = this.state;
    if (organizationShare && step && step !== "redeeming")
      return (
        <OrganizationShareStep
          share={organizationShare}
          step={step}
          message={this.state.message}
          busy={this.state.busy}
          onLogin={() => corporateLogin(organizationShare)}
          onOtherAccount={() => otherCorporateAccount(organizationShare)}
          onRetry={this.retryRedeem}
        />
      );
    if (!refusal)
      return (
        <div className={`link-page container ${translateService.key}`}>
          <img src="./assets/images/black-loading.svg" className="content-loader" />
        </div>
      );
    return (
      <LinkRefusal
        refusal={refusal}
        retrying={this.state.retrying}
        onRetry={refusal.reason === "NETWORK" && this.value ? this.open : null}
      />
    );
  }
}
